import React, { useState, useMemo, useCallback } from 'react';
import Body, { type ExtendedBodyPart } from 'react-muscle-highlighter';
import { useTheme } from '@/contexts/ThemeContext';

export interface BodyMapInjury {
  body_region: string;
  status: 'active' | 'improving' | 'resolved';
}

export interface BodyMapProps {
  injuries: BodyMapInjury[];
  onRegionClick?: (region: string) => void;
  selectedRegion?: string | null;
  interactive?: boolean;
  gender?: 'male' | 'female';
}

interface SlugMapping {
  slug: string;
  side?: 'left' | 'right';
}

const REGION_TO_SLUGS: Record<string, SlugMapping[]> = {
  head_neck: [{ slug: 'head' }, { slug: 'neck' }],
  left_shoulder: [{ slug: 'deltoids', side: 'left' }],
  right_shoulder: [{ slug: 'deltoids', side: 'right' }],
  upper_back: [{ slug: 'upper-back' }],
  lower_back: [{ slug: 'lower-back' }],
  left_hip: [{ slug: 'gluteal', side: 'left' }],
  right_hip: [{ slug: 'gluteal', side: 'right' }],
  left_quad: [{ slug: 'quadriceps', side: 'left' }],
  right_quad: [{ slug: 'quadriceps', side: 'right' }],
  left_hamstring: [{ slug: 'hamstring', side: 'left' }],
  right_hamstring: [{ slug: 'hamstring', side: 'right' }],
  left_knee: [{ slug: 'knees', side: 'left' }],
  right_knee: [{ slug: 'knees', side: 'right' }],
  left_ankle: [{ slug: 'ankles', side: 'left' }],
  right_ankle: [{ slug: 'ankles', side: 'right' }],
  left_calf: [{ slug: 'calves', side: 'left' }],
  right_calf: [{ slug: 'calves', side: 'right' }],
  left_bicep: [{ slug: 'biceps', side: 'left' }],
  right_bicep: [{ slug: 'biceps', side: 'right' }],
  left_tricep: [{ slug: 'triceps', side: 'left' }],
  right_tricep: [{ slug: 'triceps', side: 'right' }],
  left_forearm: [{ slug: 'forearm', side: 'left' }],
  right_forearm: [{ slug: 'forearm', side: 'right' }],
  left_wrist: [{ slug: 'hands', side: 'left' }],
  right_wrist: [{ slug: 'hands', side: 'right' }],
  left_shin: [{ slug: 'tibialis', side: 'left' }],
  right_shin: [{ slug: 'tibialis', side: 'right' }],
  left_groin: [{ slug: 'adductors', side: 'left' }],
  right_groin: [{ slug: 'adductors', side: 'right' }],
  left_foot: [{ slug: 'feet', side: 'left' }],
  right_foot: [{ slug: 'feet', side: 'right' }],
};

function slugToRegion(slug: string, side?: 'left' | 'right'): string | null {
  for (const [region, mappings] of Object.entries(REGION_TO_SLUGS)) {
    for (const m of mappings) {
      if (m.slug === slug && m.side === side) return region;
      if (m.slug === slug && !m.side && !side) return region;
    }
  }
  for (const [region, mappings] of Object.entries(REGION_TO_SLUGS)) {
    for (const m of mappings) {
      if (m.slug === slug && !m.side) return region;
    }
  }
  return null;
}

const STATUS_PRIORITY: Record<string, number> = { active: 0, improving: 1, resolved: 2 };

const STATUS_COLORS: Record<string, string> = {
  active: '#dc2626',
  improving: '#f59e0b',
  resolved: '#22c55e',
};

const BodyMap: React.FC<BodyMapProps> = ({
  injuries,
  onRegionClick,
  selectedRegion,
  interactive = false,
  gender = 'male',
}) => {
  const { isDark } = useTheme();
  const [view, setView] = useState<'front' | 'back'>('front');

  const regionStatuses = useMemo(() => {
    const map = new Map<string, 'active' | 'improving' | 'resolved'>();
    for (const inj of injuries) {
      const current = map.get(inj.body_region);
      if (!current || STATUS_PRIORITY[inj.status] < STATUS_PRIORITY[current]) {
        map.set(inj.body_region, inj.status);
      }
    }
    return map;
  }, [injuries]);

  // On the FRONT view, the athlete faces the viewer — so the athlete's left
  // side appears on the viewer's right side of the image, and vice versa.
  // The library renders `side: 'right'` on the viewer's right, so we need to
  // flip the anatomical side we pass in. Back view shows the athlete facing
  // away, so no flip needed.
  const flipSide = useCallback(
    (anatomicalSide: 'left' | 'right' | undefined): 'left' | 'right' | undefined => {
      if (!anatomicalSide) return anatomicalSide;
      if (view === 'front') return anatomicalSide === 'left' ? 'right' : 'left';
      return anatomicalSide;
    },
    [view],
  );

  const bodyData: ExtendedBodyPart[] = useMemo(() => {
    const parts: ExtendedBodyPart[] = [];
    const addedSlugs = new Set<string>();
    // Track which slugs already have a side-specific entry — used to detect
    // bilateral collision when selecting the opposite side of an injured muscle
    const slugsWithSide = new Set<string>();

    for (const [region, status] of regionStatuses) {
      const mappings = REGION_TO_SLUGS[region];
      if (!mappings) continue;
      const color = STATUS_COLORS[status];
      const isSelected = selectedRegion === region;
      for (const m of mappings) {
        const displaySide = flipSide(m.side);
        const key = `${m.slug}-${displaySide || ''}`;
        addedSlugs.add(key);
        if (displaySide) slugsWithSide.add(m.slug);
        parts.push({
          slug: m.slug as any,
          color,
          intensity: isSelected ? 2 : 1,
          ...(displaySide ? { side: displaySide } : {}),
        });
      }
    }

    // Highlight selected region even if it has no injuries
    if (selectedRegion && !regionStatuses.has(selectedRegion)) {
      const mappings = REGION_TO_SLUGS[selectedRegion];
      if (mappings) {
        for (const m of mappings) {
          const displaySide = flipSide(m.side);
          const key = `${m.slug}-${displaySide || ''}`;
          // Skip bilateral collisions: if the same slug already has an entry
          // for the OTHER side (e.g., right_hamstring injured), adding this
          // selection would clobber the injury highlight due to a library
          // limitation (react-muscle-highlighter uses data.find() keyed by
          // slug and only renders one side at a time). Keep the injury
          // visible instead.
          const wouldCollide = displaySide && slugsWithSide.has(m.slug) && !addedSlugs.has(key);
          if (!addedSlugs.has(key) && !wouldCollide) {
            parts.push({
              slug: m.slug as any,
              color: isDark ? '#6b7280' : '#9ca3af',
              intensity: 2,
              ...(displaySide ? { side: displaySide } : {}),
            });
          }
        }
      }
    }

    return parts;
  }, [regionStatuses, selectedRegion, isDark, flipSide]);

  const handlePress = (part: ExtendedBodyPart, side?: 'left' | 'right') => {
    if (!interactive || !onRegionClick) return;
    // Click position is in viewer coordinates — flip back to anatomical
    // coordinates on front view so we match our REGION_TO_SLUGS keys.
    const anatomicalSide = flipSide(side);
    const region = slugToRegion(part.slug as string, anatomicalSide);
    if (region) onRegionClick(region);
  };

  return (
    <div>
      <div className="flex justify-center gap-1 mb-3">
        <button
          onClick={() => setView('front')}
          className={`text-[10px] px-2 py-1 border rounded-none ${
            view === 'front'
              ? 'border-[var(--card-border)] cgm-bg-secondary cgm-text-primary'
              : 'border-transparent cgm-text-secondary'
          }`}
        >
          Front
        </button>
        <button
          onClick={() => setView('back')}
          className={`text-[10px] px-2 py-1 border rounded-none ${
            view === 'back'
              ? 'border-[var(--card-border)] cgm-bg-secondary cgm-text-primary'
              : 'border-transparent cgm-text-secondary'
          }`}
        >
          Back
        </button>
      </div>

      <div className="flex justify-center">
        <Body
          data={bodyData}
          side={view}
          gender={gender}
          scale={1}
          border={isDark ? '#333' : '#ccc'}
          defaultFill={isDark ? '#3f3f3f' : '#d4d4d4'}
          defaultStroke="none"
          defaultStrokeWidth={0}
          onBodyPartPress={handlePress}
        />
      </div>

      <div className="flex gap-3 justify-center mt-3 text-[10px]">
        <span><span style={{ color: '#dc2626' }}>●</span> Active</span>
        <span><span style={{ color: '#f59e0b' }}>●</span> Improving</span>
        <span><span style={{ color: '#22c55e' }}>●</span> Resolved</span>
      </div>
    </div>
  );
};

export default BodyMap;
